import type { RouteEvent } from '../backend/route';
import type { TranslationKey } from '../i18n';
import { parseValidMs } from './activeWaypoint';

/**
 * Journey-log event helpers per arrivesafe-api PR #180. Pure functions —
 * the JourneyTimeline component does the actual t()/render work.
 */

export const ROUTE_EVENT_TYPES = [
  'route_started',
  'battery_drop',
  'stationary',
  'companion_joined',
  'still_en_route',
  'arrived',
  'emergency',
] as const;

export type RouteEventType = (typeof ROUTE_EVENT_TYPES)[number];

export function isRouteEventType(value: unknown): value is RouteEventType {
  return typeof value === 'string' && (ROUTE_EVENT_TYPES as readonly string[]).includes(value);
}

export type JourneyEventDescription = {
  type: RouteEventType;
  labelKey: TranslationKey;
  vars: Record<string, string | number>;
  time: string | Date | null;
};

/**
 * Compact duration for the stationary row ("4 min", "1 h 20 min") —
 * consistent with EmergencyBanner's relative-time style.
 */
export function formatEventDuration(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/**
 * Events the drawer should show, oldest first. Two rules beyond sorting:
 * events with unusable timestamps sink to the end (kept, in original
 * order), and only the latest `still_en_route` heartbeat survives — each
 * one supersedes the last, and a long journey would otherwise fill the
 * drawer with near-identical rows.
 */
export function visibleJourneyEvents(events: RouteEvent[]): RouteEvent[] {
  const indexed = events.map((event, index) => ({ event, index, ms: parseValidMs(event.time) }));
  indexed.sort((a, b) => {
    const aMs = a.ms ?? Number.POSITIVE_INFINITY;
    const bMs = b.ms ?? Number.POSITIVE_INFINITY;
    if (aMs !== bMs) return aMs - bMs;
    return a.index - b.index;
  });
  const sorted = indexed.map((entry) => entry.event);
  const lastHeartbeat = sorted.reduce<RouteEvent | null>(
    (last, event) => (event.type === 'still_en_route' ? event : last),
    null,
  );
  return sorted.filter((event) => event.type !== 'still_en_route' || event === lastHeartbeat);
}

/**
 * Maps a wire event to a renderable row, or null when the row can't be
 * rendered honestly: unknown/legacy free-form types (pre-#180 routes) and
 * known types missing their defining datum (a battery_drop without a
 * battery level would read "Battery at %"). We never fake a number.
 *
 * `alertedCount` comes from route.emergency (REST reads only, per #180) —
 * the event doc itself doesn't carry it.
 */
export function describeEvent(
  event: RouteEvent,
  alertedCount?: number,
): JourneyEventDescription | null {
  if (!isRouteEventType(event.type)) return null;
  const time = event.time ?? null;
  switch (event.type) {
    case 'route_started':
      return { type: event.type, labelKey: 'timeline.routeStarted', vars: {}, time };
    case 'battery_drop':
      if (!Number.isFinite(event.battery)) return null;
      return {
        type: event.type,
        labelKey: 'timeline.batteryDrop',
        vars: { battery: Math.round(event.battery as number) },
        time,
      };
    case 'stationary':
      if (!Number.isFinite(event.durationSeconds)) return null;
      return {
        type: event.type,
        labelKey: 'timeline.stationary',
        vars: { duration: formatEventDuration(event.durationSeconds as number) },
        time,
      };
    case 'companion_joined': {
      const name = event.name?.trim();
      return name
        ? { type: event.type, labelKey: 'timeline.companionJoined', vars: { name }, time }
        : { type: event.type, labelKey: 'timeline.companionJoinedUnnamed', vars: {}, time };
    }
    case 'still_en_route':
      if (Number.isFinite(event.progress)) {
        const progress = Math.min(100, Math.max(0, Math.round(event.progress as number)));
        return {
          type: event.type,
          labelKey: 'timeline.stillEnRouteProgress',
          vars: { progress },
          time,
        };
      }
      return { type: event.type, labelKey: 'timeline.stillEnRoute', vars: {}, time };
    case 'arrived':
      return { type: event.type, labelKey: 'timeline.arrived', vars: {}, time };
    case 'emergency':
      if (typeof alertedCount === 'number' && alertedCount > 0) {
        return {
          type: event.type,
          labelKey: 'timeline.emergencyAlerted',
          vars: { count: alertedCount },
          time,
        };
      }
      return { type: event.type, labelKey: 'timeline.emergency', vars: {}, time };
  }
}
